import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { ShowHeader } from "@/common/components/ShowHeader";
import AccordionScrapRulesOverview from "../../components/AccordionOverview";
import AccordionScrapRulesEquipments from "../../components/AccordionEquipments";
import useCreateScrapRule from "../../hooks/useCreateScrapRule";
import { CreateScrapRuleSchema, createScrapRuleSchema } from "../../schemas/createScrapRule.schema";
import AccordionScrapRulesExecutionRules from "../../components/AccordionExecutionRules";
import { useMineCompany } from "@/features/company/hooks/useMineCompany";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";

const Create = () => {
  const goBack = useGoBack();
  const queryClient = useQueryClient()

  const { data: companyData } = useMineCompany();

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToCreateMaintenanceScrapRules =
    isUserPermissionsPending || hasUserPermission('Client::Maintenance::ScrapRules::Create')
    
  const {
    mutate,
  } = useCreateScrapRule({
    companyId: companyData?.id,
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['scrapRules']})
      goBack({fallback: `/maintenance-scrap-rules`})
    },
    onError: (error: any) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<CreateScrapRuleSchema>({
    resolver: zodResolver(createScrapRuleSchema)
  });

  const onSubmit: SubmitHandler<CreateScrapRuleSchema> = (data) => mutate(data);

  return (
    <form 
      className="flex flex-col h-[calc(100vh-64px)]" 
      onSubmit={handleSubmit(onSubmit)} 
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/maintenance-scrap-rules` }}
        title="Regras para desfazimento"
        subtitle="Nova regra"
        buttons={
          userHasPermissionToCreateMaintenanceScrapRules ? (
            <Button
              type="submit"
              name="submit"
            >
              Criar regra
            </Button>
          ) : undefined
        }
      />

      <ErrorMessageWrapper statusCode={userHasPermissionToCreateMaintenanceScrapRules ? 200 : 403}>
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <AccordionScrapRulesOverview />

          <TextField
            label="Nome da regra de desfazimento"
            markAsRequired
            errorMessage={errors?.display_name?.message}
            {...register('display_name')}
          />

          <AccordionScrapRulesEquipments
            control={control}
            register={register}
            errors={errors}
          />

          <AccordionScrapRulesExecutionRules
            control={control}
            register={register}
            errors={errors}
          />
        </Box>

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError
          />
        )}
      </ErrorMessageWrapper>
    </form>
  );
};

export default Create;
